/**
 * Accommodation Management Component
 * Allows admins to manage accommodation providers for tours
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { accommodationService } from '../../services/accommodationService';
import { AccommodationProvider } from '../../types/accommodation';
import { tourService } from '../../services/tourService';
import { Tour } from '../../types';
import AdminMediaUploader from './AdminMediaUploader';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const AccommodationManagement: React.FC = () => {
  const { actualTheme } = useTheme();
  const [providers, setProviders] = useState<AccommodationProvider[]>([]);
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState<AccommodationProvider | null>(null);
  const [expandedProviderId, setExpandedProviderId] = useState<string | null>(null);
  const [filterLocation, setFilterLocation] = useState<string>('all');
  const [formData, setFormData] = useState<Omit<AccommodationProvider, 'id' | 'createdAt' | 'updatedAt'>>({
    name: '',
    type: 'hotel' as AccommodationProvider['type'],
    locationName: '',
    location: {
      name: '',
      country: 'Kenya',
      region: 'East Africa',
      coordinates: { lat: 0, lng: 0 },
      address: ''
    },
    rating: 0,
    priceRange: {
      min: 0,
      max: 0
    },
    amenities: [],
    imageUrl: '',
    images: [],
    description: '',
    contactInfo: {
      phone: '',
      email: '',
      website: ''
    },
    availability: true,
    capacity: {
      rooms: 0,
      maxGuests: 0
    },
    address: '',
    primaryEmail: '',
    secondaryEmail: '',
    phone: '',
    contactPerson: '',
    apiEndpoint: '',
    checkMethod: 'email' as AccommodationProvider['checkMethod'],
    responseTimeHours: 24,
    autoConfirm: false,
    currency: 'KES',
    basePricePerNight: 0,
    pricePerPerson: 0,
    mealPlans: {
      breakfast: 0,
      halfBoard: 0,
      fullBoard: 0
    },
    maxGuests: 0,
    totalRooms: 0,
    cancellationPolicy: '',
    checkInTime: '14:00',
    checkOutTime: '11:00',
    paymentTerms: '',
    status: 'active' as AccommodationProvider['status']
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newAmenity, setNewAmenity] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [providersData, toursData] = await Promise.all([
        accommodationService.getAllProviders(),
        tourService.getAllTours()
      ]);
      setProviders(providersData);
      setTours(toursData);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      // Validation
      if (!formData.name || !formData.primaryEmail || !formData.phone || !formData.locationName) {
        setError('Please fill in all required fields (name, email, phone, and location)');
        return;
      }

      setLoading(true); // Show spinner during save
      const result = editingProvider 
        ? await accommodationService.updateProvider(editingProvider.id, formData)
        : await accommodationService.createProvider(formData);

      if (result) {
        setSuccess(`Accommodation provider ${editingProvider ? 'updated' : 'created'} successfully`);
        resetForm();
        await loadData();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        throw new Error('Failed to save provider - no data returned');
      }
    } catch (error: any) {
      console.error('Error saving provider:', error);
      setError(error.message || 'Failed to save accommodation provider');
    } finally {
      setLoading(false);
    }
  };

  const getSafeLocation = (provider: AccommodationProvider) => {
    const fallbackLat = provider.location?.coordinates?.lat ?? provider.coordinates?.lat ?? 0;
    const fallbackLng = provider.location?.coordinates?.lng ?? provider.coordinates?.lng ?? 0;
    return {
      name: provider.location?.name || provider.locationName || '',
      country: provider.location?.country || 'Kenya',
      region: provider.location?.region || 'East Africa',
      coordinates: {
        lat: fallbackLat,
        lng: fallbackLng
      },
      address: provider.location?.address || provider.address || ''
    };
  };

  const handleEdit = (provider: AccommodationProvider) => {
    setEditingProvider(provider);
    setFormData({
      name: provider.name,
      type: provider.type,
      locationName: provider.locationName,
      location: getSafeLocation(provider),
      rating: provider.rating || 0,
      priceRange: provider.priceRange || { min: 0, max: 0 },
      amenities: provider.amenities || [],
      imageUrl: provider.imageUrl || '',
      images: provider.images || [],
      description: provider.description || '',
      contactInfo: provider.contactInfo || { phone: '', email: '', website: '' },
      availability: provider.availability !== undefined ? provider.availability : true,
      capacity: provider.capacity || { rooms: 0, maxGuests: 0 },
      address: provider.address || '',
      primaryEmail: provider.primaryEmail,
      secondaryEmail: provider.secondaryEmail || '',
      phone: provider.phone,
      contactPerson: provider.contactPerson || '',
      apiEndpoint: provider.apiEndpoint || '',
      checkMethod: provider.checkMethod,
      responseTimeHours: provider.responseTimeHours,
      autoConfirm: provider.autoConfirm,
      currency: provider.currency,
      basePricePerNight: provider.basePricePerNight,
      pricePerPerson: provider.pricePerPerson || 0,
      mealPlans: provider.mealPlans ? {
        breakfast: provider.mealPlans.breakfast || 0,
        halfBoard: provider.mealPlans.halfBoard || 0,
        fullBoard: provider.mealPlans.fullBoard || 0
      } : undefined,
      maxGuests: provider.maxGuests || 0,
      totalRooms: provider.totalRooms || 0,
      cancellationPolicy: provider.cancellationPolicy || '',
      checkInTime: provider.checkInTime || '14:00',
      checkOutTime: provider.checkOutTime || '11:00',
      paymentTerms: provider.paymentTerms || '',
      status: provider.status
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this accommodation provider?')) {
      return;
    }

    try {
      const success = await accommodationService.deleteProvider(id);
      if (success) {
        setSuccess('Accommodation provider deleted successfully');
        await loadData();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to delete accommodation provider');
      }
    } catch (error: any) {
      console.error('Error deleting provider:', error);
      setError(error.message || 'Failed to delete accommodation provider');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'hotel',
      locationName: '',
      location: {
        name: '',
        country: 'Kenya',
        region: 'East Africa',
        coordinates: { lat: 0, lng: 0 },
        address: ''
      },
      rating: 0,
      priceRange: {
        min: 0,
        max: 0
      },
      amenities: [],
      imageUrl: '',
      images: [],
      description: '',
      contactInfo: {
        phone: '',
        email: '',
        website: ''
      },
      availability: true,
      capacity: {
        rooms: 0,
        maxGuests: 0
      },
      coordinates: { lat: 0, lng: 0 },
      address: '',
      primaryEmail: '',
      secondaryEmail: '',
      phone: '',
      contactPerson: '',
      apiEndpoint: '',
      checkMethod: 'email',
      responseTimeHours: 24,
      autoConfirm: false,
      currency: 'KES',
      basePricePerNight: 0,
      pricePerPerson: 0,
      mealPlans: undefined,
      maxGuests: 0,
      totalRooms: 0,
      cancellationPolicy: '',
      checkInTime: '14:00',
      checkOutTime: '11:00',
      paymentTerms: '',
      status: 'active'
    });
    setEditingProvider(null);
    setShowAddForm(false);
    setNewAmenity('');
  };

  const addAmenity = () => {
    if (newAmenity.trim() && !(formData.amenities || []).includes(newAmenity.trim())) {
      setFormData({
        ...formData,
        amenities: [...(formData.amenities || []), newAmenity.trim()]
      });
      setNewAmenity('');
    }
  };

  const removeAmenity = (amenity: string) => {
    setFormData({
      ...formData,
      amenities: (formData.amenities || []).filter(a => a !== amenity)
    });
  };

  // Filter by location name (accommodations are now location-based, not tour-specific)
  const filteredProviders = filterLocation === 'all'
    ? providers
    : providers.filter(p =>
      p.locationName?.toLowerCase().includes(filterLocation.toLowerCase()) ||
      p.location?.name?.toLowerCase().includes(filterLocation.toLowerCase())
    );

  const getTypeIcon = (type: AccommodationProvider['type']) => {
    return <BuildingOfficeIcon className="h-5 w-5" />;
  };

  const getTypeColor = (type: AccommodationProvider['type']) => {
    const colors: Record<string, string> = {
      hotel: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
      lodge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
      camp: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
      resort: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
      restaurant: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
      guesthouse: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
      other: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300'
    };
    return colors[type] || colors.other;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
          <p className={`${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
            Loading accommodation providers...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`rounded-2xl shadow-modern backdrop-blur-sm border p-6 transition-all duration-300 ${actualTheme === 'dark'
          ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
          : 'bg-white/90 border-neutral-200/50'
        }`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
              }`}>
              Accommodation Management
            </h2>
            <p className={`mt-1 text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
              }`}>
              Manage accommodation providers (hotels, lodges, camps) for tour locations
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
            className="flex items-center justify-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all duration-300 hover-magnify-sm modern-focus shadow-md"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            <span className="font-medium">Add Provider</span>
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className={`border rounded-lg p-4 transition-all duration-300 animate-fade-in ${actualTheme === 'dark'
            ? 'bg-red-900/20 border-red-800 text-red-300'
            : 'bg-red-50 border-red-200 text-red-600'
          }`}>
          <div className="flex items-start">
            <ExclamationTriangleIcon className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-4 flex-shrink-0"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className={`border rounded-lg p-4 transition-all duration-300 animate-fade-in ${actualTheme === 'dark'
            ? 'bg-green-900/20 border-green-800 text-green-300'
            : 'bg-green-50 border-green-200 text-green-600'
          }`}>
          <div className="flex items-start">
            <CheckIcon className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium">{success}</p>
            </div>
            <button
              onClick={() => setSuccess(null)}
              className="ml-4 flex-shrink-0"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Filter */}
      {!showAddForm && (
        <div className={`rounded-lg border p-4 transition-all duration-300 ${actualTheme === 'dark'
            ? 'bg-slate-800/50 border-slate-700'
            : 'bg-white border-neutral-200'
          }`}>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                }`}>
                Filter by Location
              </label>
              <input
                type="text"
                value={filterLocation === 'all' ? '' : filterLocation}
                onChange={(e) => setFilterLocation(e.target.value || 'all')}
                placeholder="Search by location name..."
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-slate-200'
                    : 'bg-white border-gray-300 text-gray-900'
                  }`}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className={`rounded-2xl shadow-modern backdrop-blur-sm border p-6 transition-all duration-300 animate-fade-in ${actualTheme === 'dark'
            ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
            : 'bg-white/90 border-neutral-200/50'
          }`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
              }`}>
              {editingProvider ? 'Edit Accommodation Provider' : 'Add Accommodation Provider'}
            </h3>
            <button
              onClick={resetForm}
              className={`p-2 rounded-lg transition-colors ${actualTheme === 'dark'
                  ? 'text-slate-400 hover:bg-slate-700 hover:text-white'
                  : 'text-neutral-600 hover:bg-neutral-100 hover:text-primary-800'
                }`}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className={`text-lg font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-800'
                }`}>
                Basic Information
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    placeholder="Provider name"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as AccommodationProvider['type'] })}
                    required
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  >
                    <option value="hotel">Hotel</option>
                    <option value="lodge">Lodge</option>
                    <option value="camp">Camp</option>
                    <option value="resort">Resort</option>
                    <option value="restaurant">Restaurant</option>
                    <option value="guesthouse">Guesthouse</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Location Name */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Location Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.locationName}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        locationName: e.target.value,
                        location: {
                          ...prev.location,
                          name: e.target.value
                        }
                      }));
                    }}
                    required
                    placeholder="e.g., Maasai Mara"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                </div>

                {/* Country */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.location.country}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        location: {
                          ...prev.location,
                          country: e.target.value
                        }
                      }));
                    }}
                    placeholder="e.g., Kenya"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                </div>

                {/* Region */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Region
                  </label>
                  <input
                    type="text"
                    value={formData.location.region}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        location: {
                          ...prev.location,
                          region: e.target.value
                        }
                      }));
                    }}
                    placeholder="e.g., East Africa"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                </div>

                {/* Coordinates - Required for distance calculation */}
                <div className="col-span-2">
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Coordinates <span className="text-red-500">*</span>
                    <span className="text-xs text-gray-500 ml-2">(Required for distance-based filtering)</span>
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                        }`}>
                        Latitude
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={formData.location.coordinates.lat}
                        onChange={(e) => {
                          const lat = parseFloat(e.target.value) || 0;
                          setFormData(prev => ({
                            ...prev,
                            location: {
                              ...prev.location,
                              coordinates: {
                                lat,
                                lng: prev.location.coordinates?.lng ?? 0
                              }
                            }
                          }));
                        }}
                        required
                        placeholder="e.g., -1.4061"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                            ? 'bg-slate-700 border-slate-600 text-slate-200'
                            : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                        }`}>
                        Longitude
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={formData.location.coordinates.lng}
                        onChange={(e) => {
                          const lng = parseFloat(e.target.value) || 0;
                          setFormData(prev => ({
                            ...prev,
                            location: {
                              ...prev.location,
                              coordinates: {
                                lat: prev.location.coordinates?.lat ?? 0,
                                lng
                              }
                            }
                          }));
                        }}
                        required
                        placeholder="e.g., 35.0081"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                            ? 'bg-slate-700 border-slate-600 text-slate-200'
                            : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      const { googleMapsService } = await import('../../services/googleMapsService');
                      if (formData.locationName) {
                        const result = await googleMapsService.geocode(formData.locationName);
                        if (result) {
                          setFormData(prev => ({
                            ...prev,
                            location: {
                              ...prev.location,
                              coordinates: {
                                lat: result.lat,
                                lng: result.lng
                              },
                              address: result.formattedAddress
                            },
                            address: result.formattedAddress
                          }));
                          setSuccess('Coordinates fetched from location name');
                        } else {
                          setError('Could not geocode location. Please enter coordinates manually.');
                        }
                      } else {
                        setError('Please enter location name first');
                      }
                    }}
                    className={`mt-2 text-xs px-3 py-1 rounded border transition-colors ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'
                        : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    📍 Get coordinates from location name
                  </button>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h4 className={`text-lg font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-800'
                }`}>
                Contact Information
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Primary Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.primaryEmail}
                    onChange={(e) => setFormData({ ...formData, primaryEmail: e.target.value })}
                    required
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    placeholder="provider@example.com"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    placeholder="+254 708 560 665"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Contact Person
                  </label>
                  <input
                    type="text"
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    placeholder="Contact person name"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Availability Check Method
                  </label>
                  <select
                    value={formData.checkMethod}
                    onChange={(e) => setFormData({ ...formData, checkMethod: e.target.value as AccommodationProvider['checkMethod'] })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  >
                    <option value="email">Email</option>
                    <option value="api">API</option>
                    <option value="phone">Phone</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Response Time (Hours)
                  </label>
                  <input
                    type="number"
                    value={formData.responseTimeHours}
                    onChange={(e) => setFormData({ ...formData, responseTimeHours: parseInt(e.target.value) || 24 })}
                    min="1"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="space-y-4">
              <h4 className={`text-lg font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-800'
                }`}>
                Pricing
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Base Price per Night
                  </label>
                  <input
                    type="number"
                    value={formData.basePricePerNight}
                    onChange={(e) => setFormData({ ...formData, basePricePerNight: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  >
                    <option value="KES">KES</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Price per Person (Optional)
                  </label>
                  <input
                    type="number"
                    value={formData.pricePerPerson}
                    onChange={(e) => setFormData({ ...formData, pricePerPerson: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                </div>
              </div>

              {/* Meal Plans */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Breakfast (Additional)
                  </label>
                  <input
                    type="number"
                    value={formData.mealPlans?.breakfast || 0}
                    onChange={(e) => setFormData({
                      ...formData,
                      mealPlans: { ...(formData.mealPlans || {}), breakfast: parseFloat(e.target.value) || 0 }
                    })}
                    min="0"
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Half Board (Additional)
                  </label>
                  <input
                    type="number"
                    value={formData.mealPlans?.halfBoard || 0}
                    onChange={(e) => setFormData({
                      ...formData,
                      mealPlans: { ...(formData.mealPlans || {}), halfBoard: parseFloat(e.target.value) || 0 }
                    })}
                    min="0"
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Full Board (Additional)
                  </label>
                  <input
                    type="number"
                    value={formData.mealPlans?.fullBoard || 0}
                    onChange={(e) => setFormData({
                      ...formData,
                      mealPlans: { ...(formData.mealPlans || {}), fullBoard: parseFloat(e.target.value) || 0 }
                    })}
                    min="0"
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="space-y-6">
              <div>
                <AdminMediaUploader
                  label="Primary Featured Image"
                  value={formData.imageUrl || ''}
                  onChange={(val: any) => setFormData({ ...formData, imageUrl: val as string })}
                  multiple={false}
                  category="accommodation"
                />
              </div>

              <div>
                <AdminMediaUploader
                  label="Image Gallery"
                  value={formData.images || []}
                  onChange={(val: any) => setFormData({ ...formData, images: val as string[] })}
                  multiple={true}
                  category="accommodation"
                />
              </div>
            </div>

            {/* Capacity */}
            <div className="space-y-4">
              <h4 className={`text-lg font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-800'
                }`}>
                Capacity
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Max Guests
                  </label>
                  <input
                    type="number"
                    value={formData.maxGuests}
                    onChange={(e) => setFormData({ ...formData, maxGuests: parseInt(e.target.value) || 0 })}
                    min="0"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                    }`}>
                    Total Rooms
                  </label>
                  <input
                    type="number"
                    value={formData.totalRooms}
                    onChange={(e) => setFormData({ ...formData, totalRooms: parseInt(e.target.value) || 0 })}
                    min="0"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-slate-200'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="space-y-4">
              <h4 className={`text-lg font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-800'
                }`}>
                Amenities
              </h4>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={newAmenity}
                  onChange={(e) => setNewAmenity(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addAmenity();
                    }
                  }}
                  placeholder="Add amenity (e.g., WiFi, Pool, Spa)"
                  className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-slate-200'
                      : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
                <button
                  type="button"
                  onClick={addAmenity}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all duration-300 hover-magnify-sm modern-focus"
                >
                  <PlusIcon className="w-5 h-5" />
                </button>
              </div>

              {(formData.amenities || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {(formData.amenities || []).map((amenity, index) => (
                    <span
                      key={index}
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${actualTheme === 'dark'
                          ? 'bg-slate-700 text-slate-200'
                          : 'bg-neutral-100 text-neutral-700'
                        }`}
                    >
                      {amenity}
                      <button
                        type="button"
                        onClick={() => removeAmenity(amenity)}
                        className="hover:text-red-500 transition-colors"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-neutral-200 dark:border-slate-700">
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-all duration-300 hover-magnify-sm modern-focus shadow-md"
              >
                {editingProvider ? 'Update Provider' : 'Create Provider'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 hover-magnify-sm modern-focus ${actualTheme === 'dark'
                    ? 'bg-slate-700 hover:bg-slate-600 text-white'
                    : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                  }`}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Providers List */}
      {!showAddForm && (
        <div className={`rounded-2xl shadow-modern backdrop-blur-sm border overflow-hidden transition-all duration-300 ${actualTheme === 'dark'
            ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
            : 'bg-white/90 border-neutral-200/50'
          }`}>
          {filteredProviders.length === 0 ? (
            <div className="p-12 text-center">
              <BuildingOfficeIcon className={`w-16 h-16 mx-auto mb-4 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-400'
                }`} />
              <p className={`text-lg font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                }`}>
                No accommodation providers found
              </p>
              <p className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                }`}>
                {filterLocation === 'all'
                  ? 'Add your first accommodation provider to get started'
                  : 'No providers found for this location'}
              </p>
            </div>
          ) : (
            <div className="space-y-4 pb-12">
              {filteredProviders.map((provider) => {
                const isExpanded = expandedProviderId === provider.id;
                
                return (
                  <div 
                    key={provider.id}
                    className={`group relative rounded-2xl border transition-all duration-500 overflow-hidden ${
                      actualTheme === 'dark' 
                        ? 'bg-slate-800/40 border-slate-700/50 hover:bg-slate-800/60 hover:border-indigo-500/30' 
                        : 'bg-white border-neutral-200/60 hover:border-indigo-300 hover:shadow-2xl shadow-indigo-500/10'
                    } ${isExpanded ? (actualTheme === 'dark' ? 'ring-1 ring-indigo-500/50' : 'ring-1 ring-indigo-400') : ''}`}
                  >
                    {/* Compact Card Content Wrapper */}
                    <div className="flex flex-col md:flex-row items-stretch min-h-[110px]">
                      
                      {/* Slim Image Thumbnail Section */}
                      <div className="w-full md:w-36 lg:w-40 relative shrink-0 overflow-hidden bg-neutral-100 dark:bg-slate-900">
                        {provider.imageUrl ? (
                          <img 
                            src={provider.imageUrl} 
                            alt={provider.name} 
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-neutral-400 dark:text-slate-600">
                            <div className="p-2 rounded-full bg-neutral-200/50 dark:bg-slate-800 mb-1">
                              {getTypeIcon(provider.type)}
                            </div>
                            <span className="text-[9px] font-bold uppercase tracking-widest text-center px-2">No Image</span>
                          </div>
                        )}
                        <div className="absolute top-2 left-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider backdrop-blur-md shadow-sm ${getTypeColor(provider.type)}`}>
                            {provider.type}
                          </span>
                        </div>
                      </div>

                      {/* Main Info Section - Compact Mode */}
                      <div className="flex-1 p-4 md:p-5 flex flex-col justify-center">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                          <div className="space-y-1">
                            <div className="flex flex-wrap items-center gap-3">
                              <h3 className={`text-base font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-white' : 'text-gray-900'} leading-tight`}>
                                {provider.name}
                              </h3>
                              
                              {/* Status & Rating moved here for compactness */}
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[9px] font-bold uppercase tracking-widest ${
                                  provider.status === 'active'
                                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200/50 dark:border-emerald-500/20'
                                    : 'bg-neutral-100 dark:bg-neutral-900/30 text-neutral-600 border border-neutral-200/50 dark:border-neutral-500/20'
                                }`}>
                                  <span className={`w-1.5 h-1.5 rounded-full mr-1 ${provider.status === 'active' ? 'bg-emerald-500' : 'bg-neutral-400'}`}></span>
                                  {provider.status}
                                </span>
                                {provider.rating > 0 && (
                                  <div className="flex items-center gap-1">
                                    <svg className="w-3 h-3 text-amber-400 fill-current" viewBox="0 0 20 20">
                                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                    <span className={`text-xs font-bold ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'}`}>{provider.rating}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 text-xs">
                              <div className="flex items-center gap-1.5 text-neutral-500 dark:text-slate-400">
                                <MapPinIcon className="w-3.5 h-3.5 text-indigo-500" />
                                <span className="font-medium whitespace-nowrap">{provider.locationName || provider.location?.name || 'No location'}</span>
                                <span className="px-1 opacity-40">•</span>
                                <span className="truncate max-w-[150px]">{provider.location?.region || 'East Africa'}, {provider.location?.country || 'Kenya'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Price & Contact Row */}
                          <div className="flex items-center flex-wrap gap-4">
                            {/* Compact Pricing */}
                            <div className="flex flex-col items-end shrink-0">
                              <div className="flex items-baseline gap-1">
                                <span className="text-[10px] font-bold text-indigo-500">USD</span>
                                <span className={`text-lg font-black ${actualTheme === 'dark' ? 'text-white' : 'text-gray-900'} leading-none`}>
                                  {(provider.basePricePerNight || 0).toLocaleString()}
                                </span>
                                <span className={`text-[10px] ${actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-500'}`}>/nt</span>
                              </div>
                            </div>

                            {/* Actions & Expansion */}
                            <div className="flex items-center gap-2">
                              <div className="flex items-center bg-neutral-100/50 dark:bg-slate-800/80 rounded-xl p-0.5 border border-neutral-200/50 dark:border-slate-700/50">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleEdit(provider); }}
                                  className={`p-1.5 rounded-lg transition-all duration-300 ${actualTheme === 'dark' ? 'text-slate-400 hover:bg-indigo-500/20 hover:text-indigo-400' : 'text-neutral-500 hover:bg-indigo-50 hover:text-indigo-600'}`}
                                  title="Edit"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDelete(provider.id); }}
                                  className={`p-1.5 rounded-lg transition-all duration-300 ${actualTheme === 'dark' ? 'text-slate-400 hover:bg-red-500/20 hover:text-red-400' : 'text-neutral-500 hover:bg-red-50 hover:text-red-600'}`}
                                  title="Delete"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); setExpandedProviderId(isExpanded ? null : provider.id); }}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all duration-300 ${
                                  isExpanded
                                    ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/30'
                                    : actualTheme === 'dark'
                                      ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                                }`}
                              >
                                {isExpanded ? 'Hide' : 'Details'}
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Subtle Contact Info Sub-row */}
                        <div className="mt-3 flex items-center gap-4 text-[11px] font-medium text-neutral-500 dark:text-slate-400 border-t border-dashed border-neutral-200 dark:border-slate-700/50 pt-2.5">
                           <div className="flex items-center gap-1.5 overflow-hidden">
                              <EnvelopeIcon className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                              <span className="truncate">{provider.primaryEmail}</span>
                           </div>
                           <div className="flex items-center gap-1.5 shrink-0">
                              <PhoneIcon className="w-3.5 h-3.5 text-indigo-500" />
                              <span>{provider.phone}</span>
                           </div>
                        </div>
                      </div>
                    </div>

                    {/* Extended Details Panel */}
                    <div className={`transition-all duration-700 ease-in-out ${isExpanded ? 'max-h-[800px] opacity-100 border-t border-dashed border-neutral-200 dark:border-slate-700' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                      <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Column 1: Info */}
                        <div className="space-y-6">
                          <div>
                            <h4 className="text-[10px] uppercase font-black tracking-widest text-indigo-500 mb-3">About</h4>
                            <p className={`text-sm leading-relaxed ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-600'}`}>
                              {provider.description || "Designed for travelers seeking authentic experiences, this property combines modern comfort with deep local flavor."}
                            </p>
                          </div>
                          {provider.amenities && provider.amenities.length > 0 && (
                            <div>
                              <h4 className="text-[10px] uppercase font-black tracking-widest text-indigo-500 mb-3">Features & Amenities</h4>
                              <div className="flex flex-wrap gap-2">
                                {provider.amenities.map(a => (
                                  <span key={a} className={`px-2.5 py-1 text-[10px] font-bold rounded-lg border transition-all duration-300 ${
                                    actualTheme === 'dark' 
                                      ? 'border-indigo-500/20 bg-indigo-500/5 text-indigo-300 hover:border-indigo-500/40' 
                                      : 'border-indigo-100 bg-indigo-50/50 text-indigo-600 hover:border-indigo-200'
                                  }`}>
                                    {a}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Column 2: Gallery */}
                        <div className="lg:col-span-2 space-y-6">
                            <div className="flex items-center justify-between">
                              <h4 className="text-[10px] uppercase font-black tracking-widest text-indigo-500">Visual Gallery</h4>
                              <span className={`text-[10px] font-bold ${actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-400'}`}>
                                {provider.images?.length || 0} Assets Found
                              </span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                              {provider.images && provider.images.length > 0 ? (
                                provider.images.map((img, idx) => (
                                  <div key={idx} className="group/img aspect-square rounded-xl overflow-hidden relative border border-neutral-200 dark:border-slate-700/50 cursor-zoom-in">
                                    <img src={img} alt={`${provider.name} gallery ${idx}`} className="w-full h-full object-cover transition-all duration-700 group-hover/img:scale-110" />
                                    <div className="absolute inset-0 bg-indigo-900/40 opacity-0 group-hover/img:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="col-span-full py-8 text-center border-2 border-dashed border-neutral-200 dark:border-slate-800 rounded-2xl">
                                  <p className="text-xs font-bold text-neutral-400 dark:text-slate-600">No additional gallery assets uploaded</p>
                                </div>
                              )}
                            </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccommodationManagement;

